import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, Play, Check, Users, Target, 
  Crown, ChartLineUp, Lightning, Compass, Heart,
  ShieldCheck, Handshake, Star, CaretRight, Cross,
  BookOpen
} from '@phosphor-icons/react';

const LandingPage = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  // Staggered animation variants
  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -60 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 60 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  const pillars = [
    {
      letter: 'P',
      title: 'Purpose',
      desc: 'God has a plan for your life. Walk in your calling. Move with divine intention and align your actions with His will.',
      icon: Compass
    },
    {
      letter: 'O',
      title: 'Ownership',
      desc: 'A man who doesn\'t lead himself cannot lead others. Take responsibility for your family, your finances, and your faith.',
      icon: ShieldCheck
    },
    {
      letter: 'W',
      title: 'Wealth',
      desc: 'Build generational abundance as good stewards. "A good man leaves an inheritance to his children\'s children." — Proverbs 13:22',
      icon: ChartLineUp
    },
    {
      letter: 'R',
      title: 'Responsibility',
      desc: 'Lead your household. Serve your community. Honor God in your business. Leave a legacy that glorifies Him.',
      icon: Crown
    }
  ];

  const features = [
    {
      title: 'Community',
      subtitle: 'Brotherhood in Christ',
      desc: 'Join a brotherhood of Black men walking in faith and building together. Share victories, seek wisdom, and grow in accountability.',
      image: 'https://images.unsplash.com/photo-1616805384781-fdcdf0328348?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBCbGFjayUyMG1hbiUyMGJ1c2luZXNzJTIwbWVldGluZyUyMGNvbW11bml0eSUyMG5ldHdvcmtpbmd8ZW58MHx8fHwxNzc2MTc5OTIwfDA&ixlib=rb-4.1.0&q=85&w=1200'
    },
    {
      title: 'Training',
      subtitle: 'Biblical Wealth Building',
      desc: 'Learn proven systems for building income and wealth, grounded in Biblical principles. Faith and business work together.',
      image: 'https://images.unsplash.com/photo-1653565685060-e15e492a7fda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxCbGFjayUyMG1hbiUyMHByb2Zlc3Npb25hbCUyMHRyYWluaW5nJTIwcHJlc2VudGF0aW9uJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc2MTc5OTIxfDA&ixlib=rb-4.1.0&q=85&w=1200'
    },
    {
      title: 'Accountability',
      subtitle: 'Iron Sharpens Iron',
      desc: 'Weekly check-ins with brothers who will challenge you, pray with you, and hold you to your word. No excuses. Just growth.',
      image: 'https://images.unsplash.com/photo-1677594333508-1b8492b36653?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxCbGFjayUyMG1lbiUyMHByb2Zlc3Npb25hbCUyMGJ1c2luZXNzJTIwdGVhbSUyMGFjY291bnRhYmlsaXR5JTIwbWVudG9yaW5nfGVufDB8fHx8MTc3NjE3OTkyMXww&ixlib=rb-4.1.0&q=85&w=1200'
    },
    {
      title: 'Resources',
      subtitle: 'Tools for Kingdom Builders',
      desc: 'Access business frameworks, financial templates, and growth strategies designed for men of faith building real wealth.',
      image: 'https://images.unsplash.com/photo-1669034750695-dfe5cad91d6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxCbGFjayUyMG1hbiUyMHByb2Zlc3Npb25hbCUyMGxhcHRvcCUyMHRlY2hub2xvZ3klMjByZXNvdXJjZXMlMjBleGVjdXRpdmV8ZW58MHx8fHwxNzc2MTc5OTIyfDA&ixlib=rb-4.1.0&q=85&w=1200'
    }
  ];

  const stats = [
    { value: '1000+', label: 'Brothers' },
    { value: '6', label: 'Courses' },
    { value: '24/7', label: 'Community' },
    { value: '100%', label: 'Faith-Driven' }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F7F7F5]">
      {/* Navigation */}
      <nav className="nav-glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-extrabold tracking-tighter text-[#C4A661]">
            POWR
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#pillars" className="text-sm font-medium text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors uppercase tracking-wider">
              Philosophy
            </a>
            <a href="#features" className="text-sm font-medium text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors uppercase tracking-wider">
              Features
            </a>
            <a href="#brotherhood" className="text-sm font-medium text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors uppercase tracking-wider">
              Brotherhood
            </a>
            <Link to="/inner-circle-sales" className="text-sm font-medium text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors uppercase tracking-wider">
              Inner Circle
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-medium text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors">
              Sign In
            </Link>
            <Link to="/signup">
              <Button className="btn-gold h-10 px-6 text-sm" data-testid="nav-join-btn">
                Join Free
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Faith-Based Scrolling Ticker */}
      <div className="relative bg-[#0A0A0A] border-b border-[#1a1a1a] overflow-hidden py-3">
        <div className="overflow-hidden w-full">
          <div 
            className="flex whitespace-nowrap animate-ticker"
            style={{ width: 'max-content' }}
          >
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="flex items-center gap-8 px-4 shrink-0">
                <span className="text-[#C4A661] text-sm font-medium tracking-wide">✟ FAITH</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#A3A3A3] text-sm tracking-wide">BROTHERHOOD</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#C4A661] text-sm font-medium tracking-wide">✟ PROVERBS 27:17</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#A3A3A3] text-sm tracking-wide">KINGDOM WEALTH</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#C4A661] text-sm font-medium tracking-wide">✟ PURPOSE</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#A3A3A3] text-sm tracking-wide">GENERATIONAL LEGACY</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#C4A661] text-sm font-medium tracking-wide">✟ STEWARDSHIP</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#A3A3A3] text-sm tracking-wide">OWNERSHIP</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#C4A661] text-sm font-medium tracking-wide">✟ COLOSSIANS 3:23</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#A3A3A3] text-sm tracking-wide">BLACK EXCELLENCE</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#C4A661] text-sm font-medium tracking-wide">✟ INTEGRITY</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#A3A3A3] text-sm tracking-wide">RESPONSIBILITY</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#C4A661] text-sm font-medium tracking-wide">✟ PROVERBS 13:22</span>
                <span className="text-[#525252]">•</span>
                <span className="text-[#A3A3A3] text-sm tracking-wide">FAMILY LEADERSHIP</span>
                <span className="text-[#525252]">•</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Section - Dark with Image of Black Men */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Parallax - Black Men in Professional Setting */}
        <motion.div 
          style={{ scale: heroScale }}
          className="absolute inset-0"
        >
          <img 
            src="https://images.pexels.com/photos/20000981/pexels-photo-20000981.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Black men in brotherhood"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/70 to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 via-transparent to-[#0A0A0A]/90" />
        </motion.div>

        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 text-center"
        >
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="show"
          >
            <motion.p variants={fadeUp} className="text-overline mb-6">
              For Black Men Who Build on Faith
            </motion.p>
            
            <motion.h1 variants={fadeUp} className="text-heading-xl mb-8">
              Purpose. Ownership.
              <br />
              <span className="text-[#C4A661]">Wealth. Responsibility.</span>
            </motion.h1>
            
            <motion.p variants={fadeUp} className="text-xl md:text-2xl text-[#A3A3A3] max-w-3xl mx-auto mb-4 leading-relaxed">
              A brotherhood of Black men committed to faith, family, and building generational wealth through Biblical principles.
            </motion.p>
            
            {/* Free Forever Badge */}
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full mb-6">
              <span className="text-[#10B981] font-bold text-sm">✓ FREE FOREVER</span>
              <span className="text-[#A3A3A3] text-sm">— No credit card required</span>
            </motion.div>
            
            <motion.p variants={fadeUp} className="text-quote text-2xl md:text-3xl mb-12">
              "Iron sharpens iron, and one man sharpens another."
              <span className="block text-sm text-[#A3A3A3] mt-2 not-italic font-sans tracking-widest">— Proverbs 27:17</span>
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup">
                <Button className="btn-gold h-14 px-10 text-lg" data-testid="hero-cta-btn">
                  Join Free Forever
                  <ArrowRight size={20} className="ml-2" weight="bold" />
                </Button>
              </Link>
              <Link to="/inner-circle-sales">
                <Button className="btn-outline h-14 px-10 text-lg" data-testid="hero-inner-circle-btn">
                  Explore Inner Circle
                </Button>
              </Link>
            </motion.div>
            
            <motion.p variants={fadeUp} className="text-sm text-[#525252] mt-4">
              Join 1,000+ brothers already building together
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3]">Scroll</span>
          <motion.div 
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-px h-12 bg-gradient-to-b from-[#C4A661] to-transparent"
          />
        </motion.div>
      </section>

      {/* Stats Bar - Dark */}
      <section className="section-dark border-y border-[#262626] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl md:text-5xl font-extrabold text-[#C4A661]">{stat.value}</p>
                <p className="text-sm uppercase tracking-[0.2em] text-[#A3A3A3] mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scripture Banner */}
      <section className="bg-[#C4A661] py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#0A0A0A] font-medium text-lg">
            <Cross size={20} className="inline-block mr-3 -mt-1" weight="bold" />
            "Commit your work to the Lord, and your plans will be established." — Proverbs 16:3
          </p>
        </div>
      </section>

      {/* POWR Pillars - Light Section */}
      <section id="pillars" className="section-light section-padding">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-overline mb-4">The Philosophy</p>
            <h2 className="text-heading-lg text-[#0A0A0A]">
              Four Pillars. <span className="text-[#C4A661]">One Kingdom Mission.</span>
            </h2>
            <p className="text-body-light max-w-2xl mx-auto mt-4">
              Built on Biblical principles for Black men ready to lead their families, 
              build wealth as good stewards, and leave a legacy that honors God.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E5E5]">
            {pillars.map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#FDFDFD] p-8 hover:bg-[#F5F5F0] transition-colors group"
              >
                <span className="text-7xl font-extrabold text-[#C4A661]/20 group-hover:text-[#C4A661]/40 transition-colors">
                  {pillar.letter}
                </span>
                <h3 className="text-xl font-bold text-[#0A0A0A] mt-4 mb-3">{pillar.title}</h3>
                <p className="text-body-light text-sm">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brotherhood Section - Dark with Image of Black Men */}
      <section id="brotherhood" className="section-dark section-padding relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1617724054780-ef6bd6b21e17?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Black man in leadership"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={slideInLeft}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <p className="text-overline mb-6">The Brotherhood</p>
              <h2 className="text-heading-lg mb-8">
                Who You Walk With 
                <span className="text-[#C4A661]"> Determines </span>
                Where You End Up.
              </h2>
              <p className="text-body-dark mb-6">
                The Bible is clear: "He who walks with wise men will be wise, but the companion 
                of fools will suffer harm." (Proverbs 13:20)
              </p>
              <p className="text-body-dark mb-8">
                POWR is a brotherhood of Black men who take their faith seriously, lead their families 
                intentionally, and build wealth God's way. Here, you're surrounded by men who pray 
                together, build together, and hold each other accountable.
              </p>
              
              <div className="space-y-4 mb-10">
                {[
                  'Daily fellowship with brothers walking in faith',
                  'Weekly prayer and accountability check-ins',
                  'Business wisdom rooted in Biblical principles',
                  'A culture of integrity, discipline, and execution'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Check size={20} className="text-[#C4A661] flex-shrink-0" weight="bold" />
                    <span className="text-[#A3A3A3]">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/signup">
                <Button className="btn-gold h-14 px-10" data-testid="brotherhood-cta-btn">
                  Join Free Forever
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <p className="text-sm text-[#525252] mt-3">100% Free • No credit card • Cancel never needed</p>
            </motion.div>

            <motion.div
              variants={slideInRight}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
                  alt="Black men in professional discussion"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 border border-[#C4A661]/30" />
              </div>
              
              {/* Floating Quote */}
              <div className="absolute -bottom-8 -left-8 bg-[#141414] border border-[#262626] p-6 max-w-xs">
                <Cross size={24} className="text-[#C4A661] mb-3" weight="fill" />
                <p className="text-quote text-xl">"As iron sharpens iron, so one man sharpens another."</p>
                <p className="text-xs text-[#A3A3A3] mt-3 uppercase tracking-widest">Proverbs 27:17</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section - Light with Tabs */}
      <section id="features" className="section-light section-padding">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-overline mb-4">What You Get</p>
            <h2 className="text-heading-lg text-[#0A0A0A]">
              Faith + Business = <span className="text-[#C4A661]">Kingdom Impact.</span>
            </h2>
            <p className="text-body-light max-w-2xl mx-auto mt-4">
              Everything you need to grow spiritually while building real wealth.
              We don't separate faith from business—we integrate them.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-8 items-start">
            {/* Feature Tabs */}
            <div className="space-y-2">
              {features.map((feature, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFeature(i)}
                  className={`w-full text-left p-6 border transition-all ${
                    activeFeature === i
                      ? 'bg-[#0A0A0A] border-[#0A0A0A] text-[#F7F7F5]'
                      : 'bg-[#F5F5F0] border-[#E5E5E5] text-[#0A0A0A] hover:border-[#C4A661]'
                  }`}
                  data-testid={`feature-tab-${i}`}
                >
                  <p className={`text-xs uppercase tracking-[0.15em] mb-2 ${
                    activeFeature === i ? 'text-[#C4A661]' : 'text-[#A3A3A3]'
                  }`}>
                    {feature.subtitle}
                  </p>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className={`text-sm ${
                    activeFeature === i ? 'text-[#A3A3A3]' : 'text-[#525252]'
                  }`}>
                    {feature.desc}
                  </p>
                </button>
              ))}
            </div>

            {/* Feature Image */}
            <div className="relative overflow-hidden bg-[#F5F5F0] border border-[#E5E5E5]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeFeature}
                  src={features[activeFeature].image}
                  alt={features[activeFeature].title}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-[450px] object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-2xl font-bold">{features[activeFeature].title}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inner Circle CTA - Dark Gold Accent */}
      <section className="section-dark section-padding relative overflow-hidden">
        {/* Gold Gradient Accent */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#C4A661]/10 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Crown size={32} className="text-[#C4A661]" weight="fill" />
                <p className="text-overline">Inner Circle</p>
              </div>
              <h2 className="text-heading-lg mb-8">
                Ready to Build 
                <span className="text-[#C4A661]"> Kingdom Wealth?</span>
              </h2>
              <p className="text-body-dark mb-4">
                "The plans of the diligent lead surely to abundance." — Proverbs 21:5
              </p>
              <p className="text-body-dark mb-8">
                The Inner Circle is for men who are done with "learning mode" and ready to execute. 
                Get access to proven income systems, direct accountability, and a brotherhood 
                that will push you toward your God-given potential.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-10">
                {[
                  'AffiliateAI System',
                  'Biblical Wealth Training',
                  'Weekly Accountability',
                  'Prayer & Support'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 border border-[#262626]">
                    <Check size={18} className="text-[#C4A661]" weight="bold" />
                    <span className="text-sm text-[#A3A3A3]">{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <Link to="/inner-circle-sales">
                  <Button className="btn-gold h-14 px-10" data-testid="inner-circle-cta-btn">
                    Learn More
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <span className="text-sm text-[#A3A3A3]">30-day guarantee</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Pricing Card */}
              <div className="bg-[#141414] border border-[#C4A661]/30 p-10">
                <p className="text-overline mb-4">Starting at</p>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-6xl font-extrabold text-[#F7F7F5]">$97</span>
                  <span className="text-[#A3A3A3]">/month</span>
                </div>
                <ul className="space-y-4 mb-8">
                  {[
                    'Full course library access',
                    'Private brotherhood community',
                    'Weekly prayer & accountability',
                    'Business + faith integration',
                    'Direct support channel'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check size={18} className="text-[#C4A661]" weight="bold" />
                      <span className="text-[#A3A3A3]">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/inner-circle-sales" className="block">
                  <Button className="w-full btn-gold h-14 text-lg" data-testid="pricing-cta-btn">
                    Join Inner Circle
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial / Quote Section - Dark */}
      <section className="section-dark-surface section-padding">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <BookOpen size={48} className="text-[#C4A661] mx-auto mb-8" weight="duotone" />
            <p className="text-quote text-3xl md:text-4xl mb-8">
              "Whatever you do, work heartily, as for the Lord and not for men, knowing that 
              from the Lord you will receive the inheritance as your reward."
            </p>
            <p className="text-[#A3A3A3] uppercase tracking-[0.2em]">Colossians 3:23-24</p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA - Dark */}
      <section className="section-dark section-padding border-t border-[#262626]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Free Forever Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full mb-8">
              <span className="text-[#10B981] font-bold">✓ 100% FREE FOREVER</span>
            </div>
            
            <h2 className="text-heading-xl mb-8">
              Ready to <span className="text-[#C4A661]">Build?</span>
            </h2>
            <p className="text-xl text-[#A3A3A3] mb-10 max-w-2xl mx-auto">
              Stop waiting. Stop planning. Start executing. Join a brotherhood of Black men 
              building real wealth, leading their families, and honoring God in their work.
            </p>
            <Link to="/signup">
              <Button className="btn-gold h-16 px-12 text-xl" data-testid="final-cta-btn">
                Join Free Forever
                <ArrowRight size={24} className="ml-3" weight="bold" />
              </Button>
            </Link>
            <p className="text-sm text-[#525252] mt-4">No credit card required • Free forever • Upgrade anytime</p>
          </motion.div>
        </div>
      </section>

      {/* Footer - Dark */}
      <footer className="section-dark py-16 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-extrabold text-[#C4A661] mb-4">POWR</h3>
              <p className="text-sm text-[#A3A3A3]">
                Purpose. Ownership. Wealth. Responsibility.
                A faith-driven brotherhood for Black men who build.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#F7F7F5]">Platform</h4>
              <ul className="space-y-3 text-sm text-[#A3A3A3]">
                <li><Link to="/community" className="hover:text-[#C4A661] transition-colors">Community</Link></li>
                <li><Link to="/courses" className="hover:text-[#C4A661] transition-colors">Courses</Link></li>
                <li><Link to="/resources" className="hover:text-[#C4A661] transition-colors">Resources</Link></li>
                <li><Link to="/videos" className="hover:text-[#C4A661] transition-colors">Video Library</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#F7F7F5]">Company</h4>
              <ul className="space-y-3 text-sm text-[#A3A3A3]">
                <li><a href="#pillars" className="hover:text-[#C4A661] transition-colors">Philosophy</a></li>
                <li><Link to="/inner-circle-sales" className="hover:text-[#C4A661] transition-colors">Inner Circle</Link></li>
                <li><Link to="/tools" className="hover:text-[#C4A661] transition-colors">Partner Tools</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#F7F7F5]">Get Started</h4>
              <ul className="space-y-3 text-sm text-[#A3A3A3]">
                <li><Link to="/signup" className="hover:text-[#C4A661] transition-colors">Join Free</Link></li>
                <li><Link to="/login" className="hover:text-[#C4A661] transition-colors">Sign In</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-[#262626] flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[#525252]">© {new Date().getFullYear()} POWR. All rights reserved.</p>
            <div className="flex items-center gap-6 text-sm text-[#525252]">
              <a href="#" className="hover:text-[#A3A3A3] transition-colors">Privacy</a>
              <a href="#" className="hover:text-[#A3A3A3] transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
